import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  EyeIcon,
  ChevronRightIcon,
  UserIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon as ChevronRightSolidIcon,
} from "@heroicons/react/24/outline";
import { Share2 } from "lucide-react";
import RightList from "../RightList";
import { getDocuments } from "../../../services/DocumentService";
import dayjs from "dayjs";
import { shareContent } from "../../../utils/share";
import { toast } from "react-toastify";

const CATEGORY_MAP = {
  tsa_mock: {
    label: "ĐỀ THI THỬ TSA",
  },
  thpt_mock: {
    label: "ĐỀ THI THỬ THPT",
  },
  tsa_docs: {
    label: "TÀI LIỆU TSA",
  },
  thpt_docs: {
    label: "TÀI LIỆU THPT",
  },
};

export default function MaterialsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const categoryKey = searchParams.get("type") || "tsa_mock";
  const category = CATEGORY_MAP[categoryKey] || CATEGORY_MAP.tsa_mock;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const categories = [
    { key: "tsa_mock", label: CATEGORY_MAP.tsa_mock.label },
    { key: "thpt_mock", label: CATEGORY_MAP.thpt_mock.label },
    { key: "tsa_docs", label: CATEGORY_MAP.tsa_docs.label },
    { key: "thpt_docs", label: CATEGORY_MAP.thpt_docs.label },
  ];

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, currentPage]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await getDocuments(currentPage, 6, categoryKey);
      setDocuments(response.data || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Lỗi khi tải tài liệu");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (key) => {
    setCurrentPage(1);
    navigate(`/materials?type=${key}`);
  };

  const handleShareDocument = (doc, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    const detailUrl = `${window.location.origin}/materials/${doc._id}`;
    shareContent({ title: doc.title, url: detailUrl });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#f9dfe8] min-h-screen">
      {/* Top breadcrumb and left sticky tab */}
      <div className="max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Left tab */}
          <div className="w-full lg:w-56 lg:shrink-0">
            <div className="bg-[#db3b6d] text-white rounded-md px-4 py-3 font-semibold shadow text-sm sm:text-base">
              TÀI LIỆU
            </div>
            <div className="mt-4 space-y-2">
              {categories.map((c) => (
                <button
                  key={c.key}
                  onClick={() => handleCategoryChange(c.key)}
                  className={`w-full text-left px-3 sm:px-4 py-2 rounded-md border transition-colors text-sm sm:text-base ${
                    c.key === categoryKey
                      ? "text-white border-[#e5a7b7] bg-[#db3b6d]"
                      : "bg-[#f8d4df] border-[#f0bccc] text-[#4b4b4b] hover:text-[#db3b6d]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right content */}
          <div className="flex-1">
            {/* Breadcrumb */}
            <div className="flex items-center text-sm text-[#4b4b4b]">
              <Link to="/" className="hover:text-[#db3b6d] font-medium">
                TRANG CHỦ
              </Link>
              <ChevronRightIcon className="w-4 h-4 mx-2 text-[#db3b6d]" />
              <span className="text-[#db3b6d] font-semibold">
                {category.label.toUpperCase()}
              </span>
            </div>

            {/* Grid */}
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
              {loading ? (
                // Loading skeleton
                [...Array(6)].map((_, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg overflow-hidden border border-[#f0bccc] bg-white shadow-xl animate-pulse"
                  >
                    <div className="bg-gray-300 p-6 min-h-[140px]" />
                    <div className="px-4 py-3 bg-[#fde7ee]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-300 rounded w-3/4" />
                          <div className="h-3 bg-gray-300 rounded w-1/2" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gray-300 ml-2" />
                      </div>
                      <div className="pt-2 border-t border-[#f0bccc]">
                        <div className="h-3 bg-gray-300 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))
              ) : documents.length > 0 ? (
                documents.map((doc) => (
                  <Link
                    to={`/materials/${doc._id}`}
                    key={doc._id}
                    className="rounded-lg overflow-hidden border border-[#f0bccc] bg-white shadow-xl block group"
                  >
                    <div className="relative bg-[#db3b6d] text-white p-6 min-h-[140px] flex items-center justify-center text-center">
                      <div className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight">
                        {doc.title}
                      </div>
                      <button
                        type="button"
                        onClick={(event) => handleShareDocument(doc, event)}
                        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 text-white hover:bg-white/30 flex items-center justify-center transition-colors"
                        aria-label="Chia sẻ tài liệu"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-4 py-3 bg-[#fde7ee]">
                      <div className="text-sm text-[#4b4b4b] flex items-center justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 font-medium">
                            <UserIcon className="w-4 h-4 text-[#db3b6d] flex-shrink-0" />
                            <span className="truncate">
                              {doc.createdBy?.fullName || "PIMAX Education"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs mt-1">
                            <CalendarIcon className="w-4 h-4 text-[#db3b6d] flex-shrink-0" />
                            <span>
                              {dayjs(doc.createdAt).format("DD/MM/YYYY")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#4b4b4b] pt-2 border-t border-[#f0bccc]">
                        <span className="flex items-center gap-1 font-medium">
                          <ArrowDownTrayIcon className="w-3.5 h-3.5 text-[#db3b6d]" />
                          {doc.numberOfDơwnload || 0} lượt tải
                        </span>
                        <span className="flex items-center gap-1 font-medium">
                          <EyeIcon className="w-3.5 h-3.5 text-[#db3b6d]" />
                          {doc.numberOfVisitors || 0} lượt xem
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-gray-500">
                  Chưa có tài liệu nào
                </div>
              )}
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-3 mb-12">
                {/* Previous button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-full bg-white border-2 border-[#db3b6d] text-[#db3b6d] hover:bg-[#db3b6d] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  aria-label="Trang trước"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>

                {/* Page numbers */}
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  // Show max 5 pages centered around current page
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 rounded-full border text-sm font-medium transition-colors ${
                          page === currentPage
                            ? "bg-[#db3b6d] text-white border-[#db3b6d]"
                            : "bg-white text-[#4b4b4b] border-[#e5a7b7] hover:text-[#db3b6d]"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} className="text-[#4b4b4b]">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                {/* Next button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-full bg-white border-2 border-[#db3b6d] text-[#db3b6d] hover:bg-[#db3b6d] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  aria-label="Trang sau"
                >
                  <ChevronRightSolidIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Right sidebar latest */}
          <div className="hidden lg:block justify-self-end">
            <RightList />
          </div>
        </div>

        <div className="mt-6 flex justify-center lg:hidden">
          <RightList />
        </div>
      </div>
    </div>
  );
}
